import React, { useEffect, useRef, useState } from 'react';
import StrategyModal from './StrategyModal';
import { getStrategyTransitionAction, shouldOpenStrategyTransition } from './strategyTransitionAction';
import './RecommendationsModal.css';
import ModalLoading from './ModalLoading';

const VERDICT_CONFIG = {
    on_track: { icon: '✅', label: 'On Track', className: 'verdict-on-track' },
    needs_attention: { icon: '⚠️', label: 'Needs Attention', className: 'verdict-needs-attention' },
    needs_revision: { icon: '🔄', label: 'Needs Revision', className: 'verdict-needs-revision' }
};

const RecommendationsModal = ({ backendService, modalService, close, existingData }) => {
    const [recommendations, setRecommendations] = useState(null);
    const [strategyContext, setStrategyContext] = useState(null);
    const [loading, setLoading] = useState(!existingData);
    const [regenerating, setRegenerating] = useState(false);
    const transitionOpenedAtRef = useRef(0);

    const handleRegenerate = async () => {
        setRegenerating(true);
        setLoading(true);
        setRecommendations(null);
        try {
            const data = await backendService.forceRegenerateRecommendation();
            if (data) {
                const content = data.data ? data.data : data;
                setRecommendations(content);
                setStrategyContext(data.strategy_context || null);
            }
        } catch (err) {
            console.error('Regeneration failed:', err);
        } finally {
            setLoading(false);
            setRegenerating(false);
        }
    };

    useEffect(() => {
        if (existingData) {
            const content = existingData.data ? existingData.data : existingData;
            setRecommendations(content);
            setStrategyContext(existingData.strategy_context || null);
            setLoading(false);
            return;
        }

        let active = true;
        backendService.fetchRecommendations().then(data => {
            if (active) {
                const content = (data && data.data) ? data.data : data;
                setRecommendations(content);
                setStrategyContext(data?.strategy_context || null);
                setLoading(false);
            }
        });
        return () => { active = false; };
    }, [backendService, existingData]);

    const openStrategy = ({ startNewOnOpen = false } = {}) => {
        if (modalService) {
            const now = Date.now();
            if (!shouldOpenStrategyTransition({ startNewOnOpen, lastOpenedAt: transitionOpenedAtRef.current, now })) return;
            if (startNewOnOpen) transitionOpenedAtRef.current = now;
            modalService.open(StrategyModal, { backendService, modalService, startNewOnOpen });
        }
    };

    const assessment = recommendations?.strategy_assessment;
    const isDojoMember = Boolean(recommendations?._isDojoMember);
    const verdict = assessment ? VERDICT_CONFIG[assessment.strategy_verdict] || null : null;
    const transitionAction = getStrategyTransitionAction(recommendations, strategyContext);
    const mediaPlanStatus = recommendations?.media_follow_up_plan_status;
    const mediaPlanActivityNames = Array.isArray(mediaPlanStatus?.activity_names)
        ? mediaPlanStatus.activity_names.filter(Boolean)
        : [];
    const mediaPlanActivityLabel = mediaPlanActivityNames.length === 1
        ? mediaPlanActivityNames[0]
        : 'your recurring media activity';

    return (
        <div className="recommendations-content">
            <div className="rec-header">
                <div className="rec-header-icon">✨</div>
                <h2>Training Insight</h2>
            </div>
            
            {loading && (
                <ModalLoading text="Analyzing your progress..." />
            )}
            
            {!loading && !recommendations && <p>Could not generate recommendations at this time.</p>}

            {!loading && recommendations && (
                <div className="rec-body">
                    {/* Strategy Nudge (no active strategy) */}
                    {!assessment && !strategyContext && recommendations.strategy_nudge && (
                        <div className="rec-strategy-banner rec-strategy-nudge">
                            <div className="rec-strategy-header">
                                <span className="rec-strategy-label">📋 Strategy</span>
                            </div>
                            <p className="rec-strategy-status">
                                {recommendations.strategy_nudge}
                            </p>
                            <button className="secondary-button rec-strategy-cta" onClick={openStrategy}>
                                {isDojoMember ? 'Open Strategy' : 'Set Up Strategy'}
                            </button>
                        </div>
                    )}

                    {/* Strategy Assessment Banner (active strategy) */}
                    {assessment && (
                        <div className="rec-strategy-banner">
                            <div className="rec-strategy-header">
                                <span className="rec-strategy-label">📋 Strategy</span>
                                {strategyContext?.is_dojo_strategy && (
                                    <span className="rec-strategy-dojo-tag">🥋 {strategyContext.dojo_name || 'Dojo'}</span>
                                )}
                                {strategyContext?.primary_goal && (
                                    <span className="rec-strategy-goal" onClick={openStrategy}>
                                        {strategyContext.primary_goal}
                                        <span className="rec-strategy-link-icon">↗</span>
                                    </span>
                                )}
                            </div>
                            <p className="rec-strategy-status">{assessment.status_summary}</p>
                            
                            {/* Milestones */}
                            {(assessment.milestones_reached?.length > 0 || assessment.milestones_upcoming?.length > 0 || assessment.milestones_overdue?.length > 0) && (
                                <div className="rec-milestones">
                                    {assessment.milestones_reached?.length > 0 && (
                                        <div className="rec-milestone-group">
                                            {assessment.milestones_reached.map((m, i) => (
                                                <div key={`r${i}`} className="rec-milestone reached">✅ {m}</div>
                                            ))}
                                        </div>
                                    )}
                                    {assessment.milestones_upcoming?.length > 0 && (
                                        <div className="rec-milestone-group">
                                            {assessment.milestones_upcoming.map((m, i) => (
                                                <div key={`u${i}`} className="rec-milestone upcoming">🎯 {m}</div>
                                            ))}
                                        </div>
                                    )}
                                    {assessment.milestones_overdue?.length > 0 && (
                                        <div className="rec-milestone-group">
                                            {assessment.milestones_overdue.map((m, i) => (
                                                <div key={`o${i}`} className="rec-milestone overdue">⚠️ {m}</div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Verdict */}
                            {verdict && (
                                <div className={`rec-verdict ${verdict.className}`}>
                                    <span className="rec-verdict-badge">
                                        {verdict.icon} {verdict.label}
                                    </span>
                                    {assessment.strategy_verdict_reason && (
                                        <span className="rec-verdict-reason">{assessment.strategy_verdict_reason}</span>
                                    )}
                                </div>
                            )}
                            {transitionAction && (
                                <button
                                    className="secondary-button rec-strategy-cta"
                                    onClick={() => openStrategy({ startNewOnOpen: transitionAction.startNewOnOpen })}
                                >
                                    {transitionAction.label}
                                </button>
                            )}
                        </div>
                    )}

                    {/* Existing sections */}
                    <div className="rec-section">
                        <h3 className="rec-section-title">
                            <span className="rec-icon">💡</span> Focus
                        </h3>
                        <p className="rec-text">{recommendations.general_advice}</p>
                    </div>

                    {Array.isArray(recommendations.workout_recommendations) && recommendations.workout_recommendations.length > 0 && (
                    <div className="rec-section">
                        <h3 className="rec-section-title">
                            <span className="rec-icon">🏋️</span> Workout Strategy
                        </h3>
                        <ul className="rec-list">
                            {recommendations.workout_recommendations && recommendations.workout_recommendations.map((rec, i) => (
                                <li key={i}>{rec}</li>
                            ))}
                        </ul>
                    </div>
                    )}

                    {Array.isArray(recommendations.meal_recommendations) && recommendations.meal_recommendations.length > 0 && (
                    <div className="rec-section">
                        <h3 className="rec-section-title">
                            <span className="rec-icon">🥦</span> Nutrition
                        </h3>
                        <ul className="rec-list">
                            {recommendations.meal_recommendations && recommendations.meal_recommendations.map((rec, i) => (
                                <li key={i}>{rec}</li>
                            ))}
                        </ul>
                    </div>
                    )}

                    {Array.isArray(recommendations.media_follow_up_requests) && recommendations.media_follow_up_requests.length > 0 && (
                        <div className="rec-section">
                            <h3 className="rec-section-title">
                                <span className="rec-icon">📸</span> Better feedback next time
                            </h3>
                            <ul className="rec-list">
                                {recommendations.media_follow_up_requests.slice(0, 3).map((request, i) => (
                                    <li key={i}>{request}</li>
                                ))}
                            </ul>
                            {mediaPlanStatus?.status === 'already_required' && (
                                <div className="rec-plan-status">
                                    <strong>Already in your plan</strong>
                                    <span>This is already required by {mediaPlanActivityLabel}, so there is no new change to approve.</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
             <div className="modal-actions">
                <button
                    className="secondary-button rec-regenerate-btn"
                    onClick={handleRegenerate}
                    disabled={regenerating || loading}
                >
                    {regenerating ? '⏳ Regenerating…' : '🔄 Regenerate'}
                </button>
                <button className="primary-button" onClick={close}>Let's do this!</button>
            </div>
        </div>
    );
};

export default RecommendationsModal;
